<template>
    <div :class="['table-responsive-sm', { 'force-mobile': forceMobileView }]">
        <!-- Mobile View -->
        <div :class="forceMobileView ? 'd-block' : 'd-block d-lg-none'">
            <div class="g-3 row mt-0 mobile-table-container">
                <div
                    class="col-12 col-sm-6 m-0"
                    v-for="record in records"
                    :key="record[idField || 'id']"
                >
                    <div class="mobile-card">
                        <div class="mobile-header" v-if="showHeaderMobile">
                            <!-- Render fields marked with mobileHeader: true -->
                            <template
                                v-for="field in mobileHeaderFields"
                                :key="field.key"
                            >
                                <slot
                                    :name="`cell-${field.key}`"
                                    :record="record"
                                >
                                    <!-- Fallback to default rendering if no slot -->
                                    <span
                                        v-if="
                                            field.type === 'icon' &&
                                            record[field.key]
                                        "
                                        class="icon-style"
                                    >
                                        <i
                                            class="fi"
                                            :class="
                                                record[field.key]?.class ||
                                                record[field.key]
                                            "
                                        ></i>
                                    </span>
                                    <span
                                        v-else-if="field.type === 'user-avatar'"
                                    >
                                        <UserAvatar
                                            :key="record.profile_image_id"
                                            :user="record"
                                            class="avatar-style"
                                            size="2.25rem"
                                            :clickable="
                                                isFieldClickable(field, record)
                                            "
                                        />
                                    </span>
                                    <span
                                        v-else-if="field.type === 'name'"
                                        class="mobile-name"
                                        :class="{
                                            'clickable-content':
                                                isFieldClickable(field, record),
                                        }"
                                        @click="
                                            clickableContentClick(field, record)
                                        "
                                    >
                                        {{
                                            record?.name ||
                                            record[field.key] ||
                                            '—'
                                        }}
                                    </span>
                                    <span
                                        v-else
                                        class="mobile-name"
                                        :class="{
                                            'clickable-content':
                                                isFieldClickable(field, record),
                                        }"
                                        @click="
                                            clickableContentClick(field, record)
                                        "
                                    >
                                        {{ record[field.key] || '—' }}
                                    </span>
                                </slot>
                            </template>
                            <!-- Default header content (if no mobileHeader fields) -->
                            <template v-if="mobileHeaderFields.length === 0">
                                <!-- User Avatar -->
                                <span
                                    v-if="
                                        fields.find(
                                            (field) =>
                                                field.type === 'user-avatar'
                                        )
                                    "
                                >
                                    <UserAvatar
                                        :key="record.profile_image_id"
                                        :user="record"
                                        class="avatar-style"
                                        size="2.25rem"
                                        :clickable="
                                            isFieldClickable(
                                                fields.find(
                                                    (field) =>
                                                        field.type ===
                                                        'user-avatar'
                                                ),
                                                record
                                            )
                                        "
                                    />
                                </span>
                                <!-- Name - can be a user-avatar or a name type -->
                                <span
                                    class="mobile-name"
                                    v-if="nameFields"
                                    :class="{
                                        'clickable-content': isFieldClickable(
                                            nameFields,
                                            record
                                        ),
                                    }"
                                    @click="
                                        clickableContentClick(
                                            nameFields,
                                            record
                                        )
                                    "
                                >
                                    {{ record?.name }}
                                </span>
                                <!-- Show Status Icon if available -->
                                <span
                                    v-if="
                                        fields.find(
                                            (field) =>
                                                field.type === 'icon' &&
                                                field.key === 'status'
                                        )
                                    "
                                    class="icon-style"
                                >
                                    <ToolTip>
                                        <template #tooltip-trigger>
                                            <i
                                                class="fi"
                                                :class="
                                                    record['status']?.class ||
                                                    record['status']
                                                "
                                            ></i>
                                        </template>
                                        <template
                                            #tooltip-content
                                            v-if="
                                                record['status']?.tooltipContent
                                            "
                                        >
                                            {{
                                                record['status']?.tooltipContent
                                            }}
                                        </template>
                                    </ToolTip>
                                </span>
                            </template>
                        </div>
                        <table class="w-100 mobile-table">
                            <tbody>
                                <tr
                                    v-for="field in mobileFields"
                                    :key="field.key"
                                    class="table-row"
                                >
                                    <!-- First column: Label -->
                                    <td
                                        class="cell mobile-label"
                                        :data-label="field.label"
                                    >
                                        {{ field.label }}
                                    </td>
                                    <!-- Second column: Value -->
                                    <td class="cell">
                                        <slot
                                            :name="`cell-${field.key}`"
                                            :field="field"
                                            :record="record"
                                        >
                                            <span
                                                v-if="field.type === 'icon'"
                                                class="icon-style"
                                            >
                                                <ToolTip>
                                                    <template #tooltip-trigger>
                                                        <i
                                                            class="fi"
                                                            :class="
                                                                record[
                                                                    field.key
                                                                ]?.class ||
                                                                record[
                                                                    field.key
                                                                ]
                                                            "
                                                        ></i>
                                                    </template>
                                                    <template
                                                        #tooltip-content
                                                        v-if="
                                                            record[field.key]
                                                                ?.tooltipContent
                                                        "
                                                    >
                                                        {{
                                                            record[field.key]
                                                                ?.tooltipContent
                                                        }}
                                                    </template>
                                                </ToolTip>
                                            </span>
                                            <span
                                                v-else-if="
                                                    field.type === 'boolean'
                                                "
                                            >
                                                <i
                                                    v-if="record[field.key]"
                                                    class="fi fi-check"
                                                ></i>
                                                <i v-else class="fi fi-x"></i>
                                            </span>
                                            <router-link
                                                v-else-if="
                                                    field.type === 'link'
                                                "
                                                :to="record[field.key]"
                                            >
                                                {{ record[field.key] }}
                                            </router-link>
                                            <a
                                                v-else-if="
                                                    field.type ===
                                                    'external-link'
                                                "
                                                :href="record[field.key]"
                                                target="_blank"
                                            >
                                                {{ record[field.key] }}
                                            </a>
                                            <span
                                                v-else-if="
                                                    field.type === 'starred'
                                                "
                                                style="cursor: pointer"
                                                @click="
                                                    $emit('star-click', record)
                                                "
                                            >
                                                <img
                                                    v-if="record[field.key]"
                                                    src="@/assets/images/icons/filled-star.svg"
                                                    alt="Filled star"
                                                />
                                                <img
                                                    v-else
                                                    src="@/assets/images/icons/star.svg"
                                                    alt="Empty star"
                                                />
                                            </span>
                                            <span
                                                v-else-if="
                                                    field.type === 'date'
                                                "
                                            >
                                                {{
                                                    formatDate(
                                                        record[field.key]
                                                    )
                                                }}
                                            </span>
                                            <span
                                                v-else-if="
                                                    field.type === 'percentage'
                                                "
                                            >
                                                {{
                                                    typeof record[field.key] ===
                                                        'number' &&
                                                    !isNaN(record[field.key])
                                                        ? record[field.key] +
                                                          '%'
                                                        : '—'
                                                }}
                                            </span>
                                            <span
                                                v-else
                                                :class="{
                                                    'clickable-content':
                                                        isFieldClickable(
                                                            field,
                                                            record
                                                        ),
                                                }"
                                                @click="
                                                    clickableContentClick(
                                                        field,
                                                        record
                                                    )
                                                "
                                            >
                                                {{ record[field.key] }}
                                            </span>
                                        </slot>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <!-- Render other buttons -->
                        <div
                            v-if="reservedSpace > 0"
                            class="actions-wrapper"
                            :data-label="'Actions'"
                        >
                            <ToolTip
                                v-for="button in otherButtonsFiltered(record)"
                                :key="button.label || button.icon"
                            >
                                <template #tooltip-trigger>
                                    <button
                                        :class="[
                                            'icon-btn',
                                            button.class || 'icon-btn-white',
                                        ]"
                                        @click="
                                            otherButtonClick(button, record)
                                        "
                                        :disabled="
                                            isOtherButtonDisabled(
                                                button,
                                                record
                                            )
                                        "
                                    >
                                        <i
                                            v-if="button.icon"
                                            class="fi"
                                            :class="button.icon"
                                        ></i>
                                        <span v-if="button.label">{{
                                            button.label
                                        }}</span>
                                    </button>
                                </template>
                                <template
                                    #tooltip-content
                                    v-if="getOtherButtonTooltip(button, record)"
                                >
                                    {{ getOtherButtonTooltip(button, record) }}
                                </template>
                            </ToolTip>

                            <ToolTip>
                                <template #tooltip-trigger>
                                    <button
                                        v-if="
                                            typeof hasDelete === 'function'
                                                ? hasDelete(record)
                                                : hasDelete
                                        "
                                        class="icon-btn icon-btn-light"
                                        @click="openModal(record)"
                                        :disabled="
                                            isDeleteButtonDisabled(record)
                                        "
                                    >
                                        <i class="fi fi-trash"></i>
                                    </button>
                                </template>
                                <template
                                    #tooltip-content
                                    v-if="getDeleteButtonTooltip(record)"
                                >
                                    {{ getDeleteButtonTooltip(record) }}
                                </template>
                            </ToolTip>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Desktop View -->
        <div class="desktop-table-wrapper">
            <table
                :class="
                    forceMobileView
                        ? 'd-none'
                        : 'd-lg-table d-none table table-borderless table-hover desktop-tabl'
                "
                ref="tableRef"
            >
                <thead class="w-100">
                    <tr>
                        <th
                            v-for="field in fields"
                            :key="field.key"
                            class="cell"
                            :style="{
                                width: calculateColumnWidth(field.width),
                            }"
                            @click="
                                sortable && field.sortable
                                    ? sort(field.key)
                                    : null
                            "
                            :class="{
                                sortable: sortable && field.sortable,
                                'text-center': field.type === 'icon',
                            }"
                            :data-label="field.label"
                        >
                            {{ field.key === 'is_starred' ? '' : field.label }}

                            <div
                                v-if="sortable && field.sortable"
                                class="d-inline-flex"
                            >
                                <i v-if="!sortKey" class="fi fi-sort"></i>
                                <i
                                    v-else-if="
                                        sortKey === field.key &&
                                        sortOrder === -1
                                    "
                                    class="fi fi-chevron-up"
                                ></i>
                                <i
                                    v-else-if="
                                        sortKey === field.key && sortOrder === 1
                                    "
                                    class="fi fi-chevron-down"
                                ></i>
                            </div>
                        </th>
                        <th
                            v-if="reservedSpace > 0"
                            class="actions-col"
                            :style="{ width: reservedSpace + 'px' }"
                            :data-label="'Actions'"
                        ></th>
                    </tr>
                </thead>
                <tbody class="w-100">
                    <tr
                        v-for="record in records"
                        class="table-row"
                        :key="record[idField || 'id']"
                        :style="record?.rowStyle"
                    >
                        <td
                            class="cell"
                            v-for="field in fields"
                            :key="field.key"
                            :data-label="field.label"
                            :style="{
                                width: calculateColumnWidth(field.width),
                            }"
                        >
                            <slot
                                :name="`cell-${field.key}`"
                                :field="field"
                                :record="record"
                            >
                                <span
                                    v-if="field.type === 'icon'"
                                    class="icon-style"
                                >
                                    <ToolTip>
                                        <template #tooltip-trigger>
                                            <i
                                                class="fi"
                                                :class="
                                                    record[field.key]?.class ||
                                                    record[field.key]
                                                "
                                            ></i>
                                        </template>
                                        <template
                                            #tooltip-content
                                            v-if="
                                                record[field.key]
                                                    ?.tooltipContent
                                            "
                                        >
                                            {{
                                                record[field.key]
                                                    ?.tooltipContent
                                            }}
                                        </template>
                                    </ToolTip>
                                </span>
                                <span
                                    v-else-if="field.type === 'starred'"
                                    style="cursor: pointer; text-align: center"
                                    @click="$emit('star-click', record)"
                                >
                                    <template v-if="record?.company_id != 0">
                                        <img
                                            v-if="record[field.key]"
                                            src="@/assets/images/icons/filled-star.svg"
                                            alt="Filled star"
                                        />
                                        <img
                                            v-else
                                            src="@/assets/images/icons/star.svg"
                                            alt="Empty star"
                                        />
                                    </template>
                                </span>
                                <span v-else-if="field.type === 'boolean'">
                                    <i
                                        v-if="record[field.key]"
                                        class="fi fi-check"
                                    ></i>
                                    <i v-else class="fi fi-x"></i>
                                </span>
                                <router-link
                                    v-else-if="field.type === 'link'"
                                    :to="record[field.key]"
                                >
                                    {{ record[field.key] }}
                                </router-link>
                                <a
                                    v-else-if="field.type === 'external-link'"
                                    :href="record[field.key]"
                                    target="_blank"
                                >
                                    {{ record[field.key] }}
                                </a>
                                <span v-else-if="field.type === 'date'">
                                    {{ formatDate(record[field.key]) }}
                                </span>
                                <span
                                    v-else-if="
                                        field.type === 'name' ||
                                        field.type === 'user-avatar'
                                    "
                                    class="d-flex align-items-center"
                                    style="gap: var(--space-2)"
                                >
                                    <UserAvatar
                                        v-if="field.type === 'user-avatar'"
                                        :key="record.profile_image_id"
                                        :user="record"
                                        class="avatar-style"
                                        size="1.75rem"
                                        :clickable="
                                            isFieldClickable(field, record)
                                        "
                                    />
                                    <span
                                        :class="{
                                            'clickable-content':
                                                isFieldClickable(field, record),
                                        }"
                                        @click="
                                            clickableContentClick(field, record)
                                        "
                                    >
                                        {{ record?.name }}
                                    </span>
                                </span>
                                <span v-else-if="field.type === 'percentage'">
                                    {{
                                        typeof record[field.key] === 'number' &&
                                        !isNaN(record[field.key])
                                            ? record[field.key] + '%'
                                            : '—'
                                    }}
                                </span>
                                <span
                                    v-else
                                    :class="{
                                        'clickable-content': isFieldClickable(
                                            field,
                                            record
                                        ),
                                    }"
                                    @click="
                                        clickableContentClick(field, record)
                                    "
                                    >{{ record[field.key] }}</span
                                >
                            </slot>
                        </td>
                        <td
                            v-if="reservedSpace > 0"
                            :data-label="'Actions'"
                            class="actions-col"
                            :style="{ width: reservedSpace + 'px' }"
                        >
                            <div class="actions-wrapper">
                                <ToolTip
                                    v-for="button in otherButtonsFiltered(
                                        record
                                    )"
                                    :key="button.label || button.icon"
                                >
                                    <template #tooltip-trigger>
                                        <button
                                            :class="[
                                                'icon-btn',
                                                button.class ||
                                                    'icon-btn-white',
                                            ]"
                                            @click="
                                                otherButtonClick(button, record)
                                            "
                                            :disabled="
                                                isOtherButtonDisabled(
                                                    button,
                                                    record
                                                )
                                            "
                                        >
                                            <i
                                                v-if="button.icon"
                                                class="fi"
                                                :class="button.icon"
                                            ></i>
                                            <span v-if="button.label">{{
                                                button.label
                                            }}</span>
                                        </button>
                                    </template>
                                    <template
                                        #tooltip-content
                                        v-if="
                                            getOtherButtonTooltip(
                                                button,
                                                record
                                            )
                                        "
                                    >
                                        {{
                                            getOtherButtonTooltip(
                                                button,
                                                record
                                            )
                                        }}
                                    </template>
                                </ToolTip>

                                <ToolTip
                                    v-if="
                                        typeof hasDelete === 'function'
                                            ? hasDelete(record)
                                            : hasDelete
                                    "
                                >
                                    <template #tooltip-trigger>
                                        <button
                                            class="icon-btn icon-btn-white"
                                            @click="openModal(record)"
                                            :disabled="
                                                isDeleteButtonDisabled(record)
                                            "
                                        >
                                            <i class="fi fi-trash"></i>
                                        </button>
                                    </template>
                                    <template
                                        #tooltip-content
                                        v-if="getDeleteButtonTooltip(record)"
                                    >
                                        {{ getDeleteButtonTooltip(record) }}
                                    </template>
                                </ToolTip>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <ConfirmModal
            :visible="isModalVisible"
            :title="`Delete ${recordType}?`"
            :confirm-button-text="`Delete ${recordType}`"
            @close="closeModal"
            @confirm="deleteRecord"
        >
            {{
                deleteDescription
                    ? deleteDescription
                    : `Are you sure you want to delete this ${this?.recordType}?`
            }}
        </ConfirmModal>
    </div>
</template>

<script>
import moment from 'moment';
import UserAvatar from '@/components/UserAvatar.vue';
import ToolTip from '@/components/fields/ToolTip.vue';
import ConfirmModal from '@/components/ConfirmModal.vue';

export default {
    name: 'TableList',
    components: {
        UserAvatar,
        ToolTip,
        ConfirmModal,
    },
    props: {
        records: {
            type: Array,
            required: true,
        },
        /**
         * Defines table columns. Each object in the array represents a column.
         * Example field object:
         * {
         *   key: 'status', // Corresponds to a key in a record object.
         *   label: 'Status', // Column header text.
         *   type: 'icon', // Determines rendering. Other types: 'boolean', 'link', 'date', 'name', 'user-avatar', 'percentage', 'starred'
         *   sortable: true, // Enables sorting on this column.
         *   width: '20%', // Column width.
         *   clickable: true, // If the cell content is clickable
         *   click: 'item-clicked' // event to be emitted
         * }
         *
         * For fields with `type: 'icon'`, the record's value for that key can be a string
         * (the icon class) or an object for more complex cases:
         * {
         *   class: 'fi-check-circle',      // The CSS class for the icon.
         *   tooltipContent: 'Task is complete' // Optional tooltip text.
         * }
         */
        fields: {
            type: Array,
            required: true,
        },
        idField: {
            type: String,
            default: 'id',
        },
        otherButtons: {
            // [ {label: string, icon: string, class: string?, click: string?, disabledState: function? (returns {disabled: boolean, tooltip: string}) } ]
            type: Array,
            required: false,
        },
        // this function is used to determine if the record has a delete button. if the function returns true, the delete button will be shown. if the function returns false, the delete button will not be shown.
        hasDelete: {
            type: [Function, Boolean],
            default: false,
        },
        deleteDisabledState: {
            type: [Function, Boolean],
            default: false,
        },
        sortable: {
            type: Boolean,
            default: false,
        },
        totalWidth: {
            type: Number,
            default: null,
        },
        minColumnWidth: {
            type: Number,
            default: 40, // Minimum column width in pixels
        },
        enforcePercentageLimits: {
            type: Boolean,
            default: true, // Whether to scale percentages if they exceed 100%
        },
        showHeaderMobile: {
            type: Boolean,
            default: true, // Whether to show the header on mobile
        },
        recordType: {
            type: String,
            default: 'record',
        },
        forceMobileView: {
            type: Boolean,
            default: false,
        },
        deleteDescription: {
            type: String,
            default: '',
        },
        forceDesktopView: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            sortKey: null,
            sortOrder: 1, // 1 for ascending, -1 for descending
            isModalVisible: false,
            currentRecord: null,
            tableWidth: 0,
            resizeTimer: null,
        };
    },
    emits: [
        'sort',
        'button-click',
        'delete',
        'star-click',
        'edit',
        'view',
        'chat',
        'items',
        'updateQuantity',
    ],
    computed: {
        // Computed property that returns the field configuration for displaying a user's name or avatar in mobile veiw
        nameFields() {
            return this.fields.find(
                (field) => field.type === 'name' || field.type === 'user-avatar'
            );
        },

        availableWidth() {
            return this.tableWidth - this.reservedSpace;
        },
        mobileHeaderFields() {
            // Get all fields marked with mobileHeader: true, in order
            return this.fields.filter((field) => field.mobileHeader === true);
        },
        mobileFields() {
            // Start with all fields
            let filteredFields = [...this.fields];

            if (!this.showHeaderMobile) return filteredFields;

            // If showHeaderMobile is true, filter out name fields, icon fields which are status fields, and user-avatar fields
            // Also filter out fields marked with mobileHeader: true (they appear in header instead)
            if (this.showHeaderMobile) {
                filteredFields = filteredFields.filter((field) => {
                    // Filter out default header fields
                    if (field.key.toLowerCase() === 'name') return false;
                    if (field.type === 'icon' && field.key === 'status')
                        return false;
                    if (field.type === 'user-avatar') return false;

                    // Filter out fields marked for mobile header (they appear in header instead)
                    if (field.mobileHeader === true) return false;

                    // Allow parent components to mark fields to hide in mobile view
                    if (field.hideInMobile) return false;

                    return true;
                });
            }

            // Check if there's a starred field
            const starredIndex = filteredFields.findIndex(
                (field) =>
                    field.key === 'is_starred' || field.type === 'starred'
            );

            // If starred field exists, move it to the end
            if (starredIndex !== -1) {
                const starredField = filteredFields.splice(starredIndex, 1)[0];
                filteredFields.push(starredField);
            }

            return filteredFields;
        },
        reservedSpace() {
            if (
                !this.otherButtons ||
                (this.otherButtons.length === 0 && !this.hasDelete)
            )
                return 0;

            // Calculate space for buttons (34px per button) + padding (8px gaps)
            const buttonWidth = 34;
            const buttonGap = 8;
            const buttonCount =
                this.otherButtons.length + (this.hasDelete ? 1 : 0);

            return (
                buttonWidth * buttonCount + (buttonCount - 1) * buttonGap + 24
            ); // 24px for padding
        },
        totalPercentage() {
            // Calculate the total percentage used by all columns
            return this.fields.reduce((total, field) => {
                if (
                    typeof field.width === 'string' &&
                    field.width.includes('%')
                ) {
                    const percentValue = parseFloat(field.width);
                    return isNaN(percentValue) ? total : total + percentValue;
                }
                return total;
            }, 0);
        },
        scaleFactor() {
            // Calculate scale factor if total percentage exceeds or is less than 100%
            if (!this.enforcePercentageLimits) return 1;

            if (this.totalPercentage === 0) return 1;

            return 100 / this.totalPercentage;
        },
    },
    watch: {
        tableWidth() {
            this.$forceUpdate();
        },
        fields: {
            handler() {
                this.$forceUpdate();
                this.$nextTick(this.updateTableWidth);
            },
            deep: true,
        },
    },
    mounted() {
        this.$nextTick(() => {
            this.updateTableWidth();
            window.addEventListener('resize', this.handleResize);
        });
    },
    updated() {
        // Check if table width needs to be updated after component updates
        this.$nextTick(() => {
            this.updateTableWidth();
        });
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.handleResize);
    },
    methods: {
        handleResize() {
            // Use debounce to prevent too many updates
            clearTimeout(this.resizeTimer);
            this.resizeTimer = setTimeout(() => {
                this.updateTableWidth();
            }, 100);
        },
        updateTableWidth() {
            if (this.$refs.tableRef) {
                const newWidth =
                    this.totalWidth || this.$refs.tableRef.clientWidth;
                if (newWidth !== this.tableWidth) {
                    this.tableWidth = newWidth;
                    // Manually trigger a resize event on window to ensure all components update
                    window.dispatchEvent(new Event('resize'));
                }
            }
        },
        calculateColumnWidth(width) {
            // If width is not provided, return 'auto'
            if (!width) return 'auto';

            // If width is a percentage
            if (typeof width === 'string' && width.includes('%')) {
                const percentValue = parseFloat(width);
                if (isNaN(percentValue)) {
                    return 'auto';
                }

                // Apply scale factor to make columns fill available space
                const adjustedPercentValue = percentValue * this.scaleFactor;

                // Calculate pixel width if table width is available
                if (this.availableWidth > 0) {
                    // Apply a minimum width to prevent columns from becoming too narrow
                    const calculatedWidth = Math.max(
                        this.minColumnWidth,
                        Math.floor(
                            (this.availableWidth * adjustedPercentValue) / 100
                        )
                    );
                    return `${calculatedWidth}px`;
                }

                // Fallback to percentage
                return `${adjustedPercentValue}%`;
            }

            // For absolute pixel widths, return as is
            return width;
        },
        otherButtonClick(btn, record) {
            this.$emit(btn.click, record);
        },
        // Toggles sorting or changes sorting column
        sort(key) {
            if (this.sortKey === key) {
                // Cycle through the states: ascending -> descending -> no sort
                if (this.sortOrder === 1) {
                    this.sortOrder = -1; // Descending
                } else if (this.sortOrder === -1) {
                    this.sortOrder = 0; // No sort
                    this.sortKey = null; // Clear the sort key
                } else {
                    this.sortOrder = 1; // Ascending
                }
            } else {
                // If it's a new key, start with ascending
                this.sortKey = key;
                this.sortOrder = 1;
            }
            this.$emit('sort', this.sortKey, this.sortOrder);
        },
        openModal(record) {
            this.isModalVisible = true;
            this.currentRecord = record;
        },
        closeModal() {
            this.isModalVisible = false;
        },
        deleteRecord() {
            this.$emit('delete', this.currentRecord);
            this.isModalVisible = false;
        },
        // Public method that can be called to refresh the table
        refresh() {
            this.$nextTick(() => {
                this.updateTableWidth();
            });
        },
        formatDate(dateString) {
            if (!dateString) return '';

            return moment.utc(dateString).format('MM/DD/YYYY');
        },
        // Debug method to help troubleshoot column width issues
        getColumnWidthInfo() {
            const info = {
                tableWidth: this.tableWidth,
                availableWidth: this.availableWidth,
                totalPercentage: this.totalPercentage,
                scaleFactor: this.scaleFactor,
                columns: this.fields.map((field) => ({
                    key: field.key,
                    width: field.width,
                    calculatedWidth: this.calculateColumnWidth(field.width),
                })),
            };
            return info;
        },

        otherButtonsFiltered(record) {
            return this.otherButtons.filter(
                // if condition is not provided, show the button. if condition is provided, show the button if the condition is true, otherwise hide the button
                (button) => !button.condition || button.condition(record)
            );
        },

        isFieldClickable(field, record) {
            if (field?.clickable) {
                if (typeof field.clickable === 'function') {
                    return field.clickable(record);
                }
                return field.clickable;
            }
            return false;
        },

        clickableContentClick(field, record) {
            if (this.isFieldClickable(field, record) && field?.click) {
                this.$emit(field.click, record);
            }
        },

        getOtherButtonsDisabledState(button, record) {
            if (
                button.disabledState &&
                typeof button.disabledState === 'function'
            ) {
                return button.disabledState(record);
            }
            return { disabled: false, tooltip: '' };
        },

        isOtherButtonDisabled(button, record) {
            return (
                this.getOtherButtonsDisabledState(button, record)?.disabled ||
                false
            );
        },

        getOtherButtonTooltip(button, record) {
            return (
                this.getOtherButtonsDisabledState(button, record)?.tooltip ||
                button.tooltip ||
                ''
            );
        },

        getDeleteButtonDisabledState(record) {
            if (
                this.deleteDisabledState &&
                typeof this.deleteDisabledState === 'function'
            ) {
                return this.deleteDisabledState(record);
            }
            return { disabled: false, tooltip: '' };
        },

        isDeleteButtonDisabled(record) {
            return this.getDeleteButtonDisabledState(record)?.disabled || false;
        },

        getDeleteButtonTooltip(record) {
            return this.getDeleteButtonDisabledState(record)?.tooltip || '';
        },
    },
};
</script>

<style scoped>
.table {
    border-collapse: collapse;
    overflow-x: auto;
    max-width: 100%;
    font-size: 0.875rem;
    width: 100%;
    /* Allow the table to adjust its layout based on content */
    table-layout: auto;
    margin-bottom: var(--space-3);
}

.cell {
    font-size: 0.875rem;
    line-height: 1.29;
    color: var(--black);
}

th {
    background-color: var(--primary-25) !important;
    padding: var(--space-2) 0.9375rem;
    font-weight: 700;
    vertical-align: middle;
    user-select: none;
    white-space: nowrap;
}

thead th:first-child {
    border-top-left-radius: var(--radius-sm);
    /* Top-left corner */
}

thead th:last-child {
    border-top-right-radius: var(--radius-sm);
    /* Top-right corner */
}

.desktop-table-wrapper {
    overflow-x: auto;
    overflow-y: visible;
    width: 100%;
    max-width: 100%;

    /* Scrollbar behavior matching SidePanel */
    &::-webkit-scrollbar {
        height: 3px;
        width: 3px;
        opacity: 0;
        transition: all 0.3s ease;
    }

    &:hover::-webkit-scrollbar {
        opacity: 1;
    }

    &::-webkit-scrollbar-track {
        background: var(--neutral-450);
        border-radius: var(--radius-sm);
    }

    &::-webkit-scrollbar-thumb {
        background: var(--neutral-600);
        border-radius: var(--radius-sm);
    }

    &::-webkit-scrollbar-thumb:hover {
        background: var(--neutral-700);
    }

    /* Firefox scrollbar styling */
    scrollbar-width: thin;
    scrollbar-color: var(--neutral-600) var(--neutral-450);
}

.desktop-table {
    display: table;
    width: 100%;
    min-width: auto !important;
    /* Add responsive container */
    overflow-x: auto;
}

td {
    padding: 0.8125rem var(--space-3);
    cursor: default;
    word-break: break-word; /* breaks long words at arbitrary points */
    overflow-wrap: anywhere; /* modern, allows breaks anywhere */
    hyphens: auto;
}

tr:nth-child(even) {
    background-color: var(--neutral-400);
}

tr:nth-child(odd) {
    background-color: var(--white);
}

th.sortable {
    cursor: pointer;
}

th div {
    font-size: 1rem;
    color: var(--neutral-900);
    min-width: 16px;
    margin-left: 0.25rem;
}

.table-row {
    height: auto;
    max-height: 4.5rem;
    line-height: 1.5;
}

.table-row td {
    height: auto;
    padding: 0.3125rem var(--space-3);
    max-height: 4.5rem;
    vertical-align: middle;
}

/* Apply truncation only to text content */
.table-row td span:not(.fi):not([class^='icon']) {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: normal;
}

/* Force mobile view styles when .force-mobile class is present */
.force-mobile thead {
    display: none;
}
.force-mobile tr {
    border: 1px solid var(--neutral-600);
    border-radius: var(--radius-sm);
    border-collapse: collapse;
    padding: var(--space-1);
}
.force-mobile td {
    border: 1px solid var(--neutral-600);
    border-collapse: collapse;
    padding: var(--space-1) var(--space-2);
}
.force-mobile td:first-child {
    background-color: var(--primary-25);
    width: 30%;
    min-width: 100px;
    white-space: normal !important;
    overflow-wrap: normal !important;
    word-break: normal !important;
}
.force-mobile td:last-child {
    width: 70%;
    max-width: 0;
    overflow: hidden;
    padding-right: 0;
}
.force-mobile td:last-child span:not(.fi):not([class^='icon']),
.force-mobile td:last-child a {
    display: -webkit-box;
    -webkit-line-clamp: 1;
    line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    word-break: break-word;
    text-align: right;
    padding-right: var(--space-2);
}
.force-mobile .mobile-card {
    border-radius: var(--radius-lg);
    padding: var(--space-3);
    background-color: var(--neutral-400);
    min-width: fit-content;
    margin-bottom: var(--space-4);
}
.force-mobile .mobile-header {
    display: flex;
    align-items: center;
    margin-bottom: 0.75rem;
}
.force-mobile .mobile-name {
    font-size: 1.125rem;
    line-height: var(--space-6);
    font-weight: 600;
    word-break: break-word; /* breaks long words at arbitrary points */
    overflow-wrap: anywhere; /* modern, allows breaks anywhere */
    hyphens: auto;
}
.force-mobile .mobile-status {
    font-size: 1.5rem;
}
.force-mobile .actions-wrapper {
    justify-content: flex-start;
}
.force-mobile .mobile-label {
    font-weight: 700;
}
.force-mobile .mobile-table {
    margin-bottom: var(--space-4);
    table-layout: fixed;
    border-collapse: collapse;
}
.force-mobile .mobile-table-container {
    margin-bottom: calc(-1 * var(--space-4));
}
.force-mobile.table-responsive-sm {
    overflow: hidden !important;
    overflow-x: hidden !important;
    overflow-y: hidden !important;
}

.table-responsive-sm {
    width: 100%;
}

.actions-wrapper {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: var(--space-2);
}

.icon-style {
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
}

.icon-style i {
    font-size: 1.125rem;
}

.user-avatar {
    width: 2.5rem;
    height: 2.5rem;
}

.clickable-content {
    color: var(--primary-400);
    font-weight: 600;
    cursor: pointer;
}

.clickable-content:hover {
    text-decoration: underline;
    text-decoration-skip-ink: none;
    text-underline-position: from-font;
    color: var(--black);
}

@media (max-width: 991px) {
    thead {
        display: none;
        /* Hide table headers on small screens */
    }

    tr {
        border: 1px solid var(--neutral-600);
        border-radius: var(--radius-sm);
        border-collapse: collapse;
        padding: var(--space-1);
    }

    td {
        border: 1px solid var(--neutral-600);
        border-collapse: collapse;
        padding: var(--space-1) var(--space-2);
    }

    td:first-child {
        background-color: var(--primary-25);
        width: 30%;
        min-width: 100px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    td:last-child {
        width: 70%;
        max-width: 0;
        /* overflow: hidden; */
    }

    td:last-child {
        padding-right: 0;
        /* hack to allow ellipsis to view */
    }

    td:last-child span:not(.fi):not([class^='icon']),
    td:last-child a {
        display: -webkit-box;
        -webkit-line-clamp: 1;
        line-clamp: 1;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
        word-break: break-word;
        text-align: right;
        padding-right: var(--space-2);
    }

    .mobile-card {
        border-radius: var(--radius-lg);
        padding: var(--space-3);
        background-color: var(--neutral-400);
        min-width: fit-content;
        margin-bottom: var(--space-4);
        overflow: visible !important;
        position: relative;
    }

    .mobile-header {
        display: flex;
        align-items: center;
        margin-bottom: 0.75rem;
        gap: var(--space-2);
    }

    .mobile-name {
        font-size: 1.125rem;
        line-height: var(--space-6);
        font-weight: 600;
        word-break: break-word; /* breaks long words at arbitrary points */
        overflow-wrap: anywhere; /* modern, allows breaks anywhere */
        hyphens: auto;
    }

    .mobile-status {
        font-size: 1.5rem;
    }

    .actions-wrapper {
        justify-content: flex-end;
    }

    .mobile-label {
        font-weight: 700;
        white-space: normal !important;
        overflow-wrap: normal;
        word-break: normal !important;
    }

    .mobile-table {
        margin-bottom: var(--space-4);
        table-layout: fixed;
        border-collapse: collapse;
    }

    .mobile-table-container {
        /* hack to reduce the bottom margin of the last card */
        margin-bottom: calc(-1 * var(--space-4));
        overflow: visible !important;
    }

    .table-responsive-sm {
        overflow: visible !important;
    }
}
</style>
